/**************************************************************************************************
 *
 *       @file ./config-utils.ts
 *
 *       Get config from this location. Ensure config is frozen top to bottom,
 *       and that it includes both the client & shared config, and nothing else
 *
 */

/**************************************** PROJECT MODULES *****************************************/
import {configClient, devNS} from '../../../config/app/config-client';
import {configShared, SupportedLang} from '../../../config/app/config-shared';
import {assignFrozenClone} from 'mad-utils/lib/react';
import {isDevelopment} from 'env-var-helpers';
import deepMerge from 'deepmerge';

/************************************ EXPORT TYPE DEFINITIONS *************************************/
/**
 * Config shape
 */
export type ConfigClientType = typeof configClient;
export type ConfigSharedType = typeof configShared;
export type SyncServerUrls = typeof configClient.urls.syncServer;
export type SyncServerRouteNames = keyof typeof configClient.urls.syncServer;

export {SupportedLang};

/********************************************* EXPORT *********************************************/
/**
 * List of supported languages, in 2-letter code form (['en', 'fr'])
 */
export const supportedLangs = configShared.i18n.supportedLangs as SupportedLang[];

type ConfigClientSharedType = typeof configClient & typeof configShared;
let _config: ConfigClientSharedType;

/**
 * Export client config object itself (memoizes)
 * @return {Object} Merged content of client config & shared config objects
 */
export const config = (() => {
    _config =
        _config ||
        (assignFrozenClone<ConfigClientSharedType>(
            deepMerge.all([{}, configClient, configShared], {clone: true})
        ) as Readonly<ConfigClientType & ConfigSharedType>);
    return _config;
})();

/**
 * Export raw configs
 */
export {configClient as _configClient, configShared as _configShared};
export {devNS};

/**************************************** RUN ON FILE LOAD ****************************************/
/**
 * Store config in the convenience "dev" namespace if dev mode is on
 */
(function storeConfigInDevNamespace() {
    if (isDevelopment) {
        if (
            typeof window[devNS] !== 'undefined' &&
            typeof window[devNS] === 'object' &&
            window[devNS] !== null
        ) {
            window[devNS].configClient = configClient;
        } else {
            window[devNS] = {configClient};
        }
    }
})();
